"use client";

import React from 'react';
import {
  BoltIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LinkIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import type {
  DecisionTraceCheck,
  DecisionTraceModel,
  RunDetailsModel,
} from '@/lib/agentDecisionTrace';

type DecisionTracePanelProps = {
  trace: DecisionTraceModel | null;
  className?: string;
  defaultOpen?: boolean;
};

type RunDetailsPanelProps = {
  details: RunDetailsModel | null;
  className?: string;
  defaultOpen?: boolean;
};

const CHECK_TONES: Record<DecisionTraceCheck['status'], string> = {
  passed: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  warning: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  blocked: 'border-destructive/30 bg-destructive/10 text-destructive',
  info: 'border-border/60 bg-muted/30 text-muted-foreground',
};

function formatMs(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

function shortId(value?: string): string {
  if (!value) return '';
  return value.length > 18 ? `${value.slice(0, 8)}...${value.slice(-6)}` : value;
}

function DetailShell({
  title,
  eyebrow,
  children,
  className,
  defaultOpen = false,
}: {
  title: string;
  eyebrow: string;
  children: React.ReactNode;
  className?: string;
  defaultOpen?: boolean;
}) {
  return (
    <details
      className={cn(
        'group mt-3 rounded-xl border border-border/50 bg-background/65 px-3 py-2 text-foreground shadow-sm',
        className,
      )}
      open={defaultOpen}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{eyebrow}</p>
        </div>
        <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="mt-3 space-y-3">{children}</div>
    </details>
  );
}

export function DecisionTracePanel({ trace, className, defaultOpen = false }: DecisionTracePanelProps) {
  if (!trace) return null;

  return (
    <DetailShell
      title="Decision trace"
      eyebrow="Observable route, sources, checks, and blockers"
      className={className}
      defaultOpen={defaultOpen}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {(trace.route || trace.routeSource) && (
          <div className="rounded-lg border border-border/50 bg-muted/20 p-3">
            <div className="flex items-center gap-2">
              <BoltIcon className="h-4 w-4 text-primary" />
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Route</p>
            </div>
            <p className="mt-1 text-sm font-medium text-foreground">{trace.route || 'Selected route'}</p>
            {trace.routeSource ? <p className="text-xs text-muted-foreground">Source {trace.routeSource}</p> : null}
          </div>
        )}

        {trace.confidenceBand && (
          <div className="rounded-lg border border-border/50 bg-muted/20 p-3">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="h-4 w-4 text-primary" />
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Confidence</p>
            </div>
            <p className="mt-1 text-sm font-medium capitalize text-foreground">{trace.confidenceBand}</p>
            {typeof trace.confidenceScore === 'number' ? (
              <p className="text-xs text-muted-foreground">{trace.confidenceScore.toFixed(2)}</p>
            ) : null}
          </div>
        )}
      </div>

      {trace.sources.length ? (
        <div>
          <div className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-primary" />
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Sources and citations
            </p>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {trace.sources.map((source) => (
              <span
                key={`${source.id}:${source.label}`}
                className="max-w-full rounded-full border border-border/60 bg-muted/30 px-2 py-1 text-xs text-muted-foreground"
                title={source.detail || source.url}
              >
                {source.label.length > 68 ? `${source.label.slice(0, 65)}...` : source.label}
                {typeof source.confidence === 'number' ? ` (${source.confidence.toFixed(2)})` : ''}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {trace.assumptions.length ? (
        <div>
          <div className="flex items-center gap-2">
            <InformationCircleIcon className="h-4 w-4 text-primary" />
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assumptions</p>
          </div>
          <ul className="mt-2 space-y-1">
            {trace.assumptions.map((assumption) => (
              <li key={assumption} className="text-xs text-muted-foreground">
                {assumption}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {trace.checks.length ? (
        <div>
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-4 w-4 text-primary" />
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Checks run</p>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {trace.checks.map((check) => (
              <span
                key={`${check.label}:${check.detail || check.status}`}
                className={cn('rounded-full border px-2 py-1 text-xs', CHECK_TONES[check.status])}
              >
                {check.label}
                {check.detail ? ` · ${check.detail}` : ''}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {trace.blockers.length ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-4 w-4 text-destructive" />
            <p className="text-xs font-semibold uppercase tracking-wide text-destructive">Blockers</p>
          </div>
          <ul className="mt-2 space-y-1">
            {trace.blockers.map((blocker) => (
              <li key={blocker} className="text-xs text-destructive">
                {blocker}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {trace.latencies.length ? (
        <div>
          <div className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4 text-primary" />
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Latency</p>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {trace.latencies.map((latency) => (
              <span key={`${latency.label}:${latency.ms}`} className="rounded-full border border-border/60 bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
                {latency.label} {formatMs(latency.ms)}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </DetailShell>
  );
}

export function RunDetailsPanel({ details, className, defaultOpen = false }: RunDetailsPanelProps) {
  if (!details) return null;

  return (
    <DetailShell
      title="Run details"
      eyebrow="IDs, route, tools, retrieval, approvals, and persistence"
      className={className}
      defaultOpen={defaultOpen}
    >
      <div className="grid gap-2 text-xs sm:grid-cols-2">
        {details.runId ? (
          <div className="rounded-lg border border-border/50 bg-muted/20 p-2">
            <p className="font-medium text-foreground">Run ID</p>
            <p className="mt-1 text-muted-foreground">{shortId(details.runId)}</p>
          </div>
        ) : null}
        {details.threadId ? (
          <div className="rounded-lg border border-border/50 bg-muted/20 p-2">
            <p className="font-medium text-foreground">Thread ID</p>
            <p className="mt-1 text-muted-foreground">{shortId(details.threadId)}</p>
          </div>
        ) : null}
        {details.route ? (
          <div className="rounded-lg border border-border/50 bg-muted/20 p-2">
            <p className="font-medium text-foreground">Route</p>
            <p className="mt-1 text-muted-foreground">{details.route}</p>
          </div>
        ) : null}
        {details.artifactTitle || details.artifactId ? (
          <div className="rounded-lg border border-border/50 bg-muted/20 p-2">
            <p className="font-medium text-foreground">Artifact</p>
            <p className="mt-1 text-muted-foreground">{details.artifactTitle || shortId(details.artifactId)}</p>
          </div>
        ) : null}
        {typeof details.retrievalCount === 'number' ? (
          <div className="rounded-lg border border-border/50 bg-muted/20 p-2">
            <p className="font-medium text-foreground">Retrieval</p>
            <p className="mt-1 text-muted-foreground">{details.retrievalCount} sources</p>
          </div>
        ) : null}
        {typeof details.citationCount === 'number' ? (
          <div className="rounded-lg border border-border/50 bg-muted/20 p-2">
            <p className="font-medium text-foreground">Citations</p>
            <p className="mt-1 text-muted-foreground">{details.citationCount}</p>
          </div>
        ) : null}
        {details.persistenceStatus ? (
          <div className="rounded-lg border border-border/50 bg-muted/20 p-2">
            <p className="font-medium text-foreground">Persistence</p>
            <p className="mt-1 text-muted-foreground">{details.persistenceStatus}</p>
          </div>
        ) : null}
      </div>

      {details.toolCalls.length ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tool calls</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {details.toolCalls.map((call) => (
              <span key={`${call.id}:${call.label}`} className="rounded-full border border-border/60 bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
                {call.label}
                {call.status ? ` · ${call.status}` : ''}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {details.approvalDecisions.length ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Approval decisions</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {details.approvalDecisions.map((decision) => (
              <span key={decision} className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-700 dark:text-emerald-300">
                {decision}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </DetailShell>
  );
}
